import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { motion, useAnimation } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import { useScrollContainer } from '../../context/ScrollContainerContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';
import { formatTND } from '../../lib/currency';
import { getActivePromo, getEffectivePrice, getPromoFlashColor, getPromoToneClasses, sortItemsByPromo } from '../../lib/promo';
import { shouldShowPrices } from '../../lib/priceVisibility';

async function fetchJsonOrThrow(url) {
  const response = await fetch(url);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error('Request failed');
  }
  return data;
}

const containerVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function HomeSparePartsSection() {
  const { language, t } = useI18n();
  const sectionRef = useRef(null);
  const controls = useAnimation();
  const scrollContainerRef = useScrollContainer();
  const hasAnimatedRef = useRef(false);
  const [parts, setParts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const target = sectionRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        root: scrollContainerRef?.current || null,
        rootMargin: '-120px 0px -120px 0px',
        threshold: 0.18,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    if (!isInView || hasAnimatedRef.current) return undefined;

    hasAnimatedRef.current = true;
    const frame = requestAnimationFrame(() => {
      controls.start('visible');
    });

    return () => cancelAnimationFrame(frame);
  }, [controls, isInView]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setFetchError(false);

    fetchJsonOrThrow(`/api/spare-parts?lang=${language}&paginated=true&page=1&pageSize=4`)
      .then((data) => {
        if (!isMounted) return;
        if (Array.isArray(data?.items)) {
          setParts(data.items);
          return;
        }
        setParts(Array.isArray(data) ? data.slice(0, 4) : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setFetchError(true);
        setParts([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [language, retryCount]);

  const featured = useMemo(() => sortItemsByPromo(parts).slice(0, 4), [parts]);
  const spareTitleParts = useMemo(() => {
    const line1 = String(t('sparePartsPage.hero.titleLine1', '') || '').trim();
    const line2 = String(t('sparePartsPage.hero.titleLine2', '') || '').trim();
    if (line1 || line2) {
      return { line1: line1 || String(t('sparePartsPage.hero.title') || '').trim(), line2 };
    }

    const full = String(t('sparePartsPage.hero.title') || '').trim();
    const words = full.split(/\s+/).filter(Boolean);
    if (words.length <= 1) return { line1: full, line2: '' };
    return {
      line1: words.slice(0, -1).join(' '),
      line2: words[words.length - 1],
    };
  }, [t]);

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-[var(--dash-main)] py-24">
      <div className="absolute inset-0 opacity-80 bg-[url('/bg/2.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <motion.div
        animate={controls}
        className="container relative z-10 mx-auto px-6 lg:px-12"
        variants={containerVariants}
        initial="hidden"
      >
        <motion.div key={language} className="mb-10 flex items-end justify-between gap-6" variants={itemVariants}>
          <motion.div variants={itemVariants}>
            <AnimatedEyebrow
              className="mb-6"
              borderClassName="border-[var(--dash-secondary)]"
              surfaceClassName="bg-[var(--dash-black)]"
              dotClassName="bg-[var(--dash-main)] shadow-[0_0_8px_#ffffff]"
            >
              {t('header.nav.spareParts')}
            </AnimatedEyebrow>
            <h2 className="mt-4 text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
              <span className="block">{spareTitleParts.line1}</span>
              {spareTitleParts.line2 ? (
                <span className="block font-black text-white">{spareTitleParts.line2}</span>
              ) : null}
            </h2>
          </motion.div>
          <motion.div variants={itemVariants}>
            <Link
              href="/spare-parts"
              className="group hidden items-center gap-3 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all hover:border-white hover:bg-white hover:text-[var(--dash-main)] md:inline-flex"
            >
              <span>{t('home.catalog.viewAll')}</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </motion.div>

        {isLoading ? (
          <motion.div animate={controls} className="grid gap-5 md:grid-cols-2 lg:grid-cols-4" variants={containerVariants}>
            {Array.from({ length: 4 }).map((_, i) => (
              <motion.div key={i} variants={itemVariants} className="h-64 animate-pulse rounded-2xl border border-white/10 bg-white/5" />
            ))}
          </motion.div>
        ) : fetchError ? (
          <motion.div variants={itemVariants} className="flex flex-col items-center gap-4 rounded-2xl border border-white/10 bg-white/5 py-16 text-center">
            <p className="text-sm font-semibold text-white/60">
              {t('common.errorLoadingData', 'Unable to load spare parts right now.')}
            </p>
            <button
              type="button"
              onClick={() => setRetryCount((c) => c + 1)}
              className="rounded-full border border-white/30 bg-white/10 px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white transition-all hover:border-white hover:bg-white hover:text-[var(--dash-main)]"
            >
              {t('common.retry', 'Retry')}
            </button>
          </motion.div>
        ) : (
          <motion.div
            animate={controls}
            key={`featured-${language}`}
            className="grid gap-5 md:grid-cols-2 lg:grid-cols-4"
            variants={containerVariants}
          >
            {featured.map((part, index) => {
              const activePromo = getActivePromo(part, language);
              const promoTone = getPromoToneClasses(activePromo?.colorTone);
              const basePrice = Number(part?.price ?? part?.unitPrice ?? part?.amount ?? 0);
              const effectivePrice = getEffectivePrice(part, language);
              const hasDiscount = activePromo?.kind === 'price' && effectivePrice < basePrice;
              const showPrices = shouldShowPrices();

              return (
                <motion.article
                  key={`${part?.id || part?._id || part?.slug || part?.name || 'part'}-${index}`}
                  variants={itemVariants}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[var(--dash-black)] transition-all duration-300 hover:border-[var(--dash-main)]/40"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#111]">
                    <ImageOnlySkeletonLoader
                      image={part?.images?.[0] || '/placeholders/1.webp'}
                      mode="native"
                      alt={part?.name ? `${part.name} spare part` : 'Featured spare part'}
                      loading="lazy"
                      containerClassName="h-full w-full"
                      imgClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {activePromo ? (
                      <span
                        className={`promo-badge-flash absolute right-3 top-3 overflow-visible rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] shadow-sm ${promoTone.badge}`}
                        style={{ '--promo-flash-color': getPromoFlashColor(activePromo?.colorTone) }}
                      >
                        {activePromo.label || activePromo.title || t('common.promo')}
                      </span>
                    ) : null}
                  </div>
                  <div className="p-5">
                    <h3 className="line-clamp-2 min-h-[3rem] text-base font-semibold text-white">
                      {part?.name || 'Spare Part'}
                    </h3>
                    {showPrices ? (
                      hasDiscount ? (
                        <div className="mt-3 space-y-1">
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/40 line-through">
                            {formatTND(basePrice)}
                          </p>
                          <p className="text-sm font-bold text-[var(--dash-other)]">
                            {formatTND(effectivePrice)}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-3 text-sm font-bold text-[var(--dash-other)]">
                          {formatTND(basePrice)}
                        </p>
                      )
                    ) : (
                      <p className="mt-3 text-sm font-bold text-[var(--dash-other)]">
                        {t('common.priceOnRequest')}
                      </p>
                    )}
                    <Link
                      href={`/spare-parts/${part?.id || part?._id || ''}`}
                      className="mt-4 inline-flex items-center gap-2 rounded-xl border border-white/20 px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/82 transition-all duration-300 hover:border-[var(--dash-main)] hover:text-white"
                    >
                      <ArrowUpRight className="h-3.5 w-3.5 text-[var(--dash-main)]" />
                      {t('sparePartsPage.grid.viewDetails')}
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        )}

        <motion.div className="mt-8 md:hidden" variants={itemVariants}>
          <Link
            href="/spare-parts"
            className="group inline-flex items-center gap-3 rounded-full border border-white/30 bg-white/10 px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white transition-all hover:border-white hover:bg-white hover:text-[var(--dash-main)]"
          >
            <span>{t('home.catalog.viewAll')}</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}
